import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { VisitorCountryService } from '../visitor-country.service';
import { HomePageVisibilitySettings } from 'src/app/admin/tf-admin/tf-configuration/tf-environments/tf-environments.model';
import {
  SkipPublicPageApiRecord,
  SkipPublicPageResolveResponse,
  SkipPublicPageSavePayload
} from './skip-public-page.model';
import {
  apiRecordsToUiSettings,
  resolveHomePageAccess,
  uiSettingsToSavePayload
} from './skip-public-page.mapper';

@Injectable({
  providedIn: 'root'
})
export class SkipPublicPageService {
  private readonly baseUrl = `${environment.apiUrlHost}/CP/skip-public-page`;

  private settingsCache: HomePageVisibilitySettings | null = null;
  private settingsRequest$: Observable<HomePageVisibilitySettings> | null = null;
  private readonly resolveCache = new Map<string, boolean>();

  constructor(
    private http: HttpClient,
    private visitorCountry: VisitorCountryService
  ) {}

  getSettings(resolveCountryName: (code: string) => string): Observable<HomePageVisibilitySettings> {
    if (this.settingsCache) {
      return of(this.settingsCache);
    }

    if (!this.settingsRequest$) {
      this.settingsRequest$ = this.http.get<SkipPublicPageApiRecord[]>(`${this.baseUrl}/`).pipe(
        map((records) => apiRecordsToUiSettings(records, resolveCountryName)),
        tap((settings) => {
          this.settingsCache = settings;
        }),
        catchError((error) => {
          console.error('Error loading skip public page settings', error);
          const fallback: HomePageVisibilitySettings = {
            enabled: false,
            countryOverrides: []
          };
          return of(fallback);
        }),
        shareReplay(1)
      );
    }

    return this.settingsRequest$;
  }

  saveSettings(
    settings: HomePageVisibilitySettings,
    resolveCountryName: (code: string) => string
  ): Observable<HomePageVisibilitySettings> {
    const payload: SkipPublicPageSavePayload = uiSettingsToSavePayload(settings);

    return this.http.put<SkipPublicPageApiRecord[]>(`${this.baseUrl}/`, payload).pipe(
      map((records) => apiRecordsToUiSettings(records, resolveCountryName)),
      tap((saved) => {
        this.settingsCache = saved;
        this.settingsRequest$ = of(saved);
        this.resolveCache.clear();
      }),
      catchError((error) => {
        console.error('Error saving skip public page settings', error);
        throw error;
      })
    );
  }

  /**
   * For anonymous visitors: detect country from public IP, then resolve home-page access.
   * Never uses browser locale or logged-in profile — IP geolocation only.
   */
  canDisplayHomePageForVisitorIp(): Observable<boolean> {
    return this.visitorCountry.detect().pipe(
      switchMap((geo) => this.canDisplayHomePage(geo.countryCode))
    );
  }

  /**
   * GET /CP/skip-public-page/resolve/?country_code=US when a country is known;
   * GET /CP/skip-public-page/resolve/ without params falls back to general setting only.
   */
  canDisplayHomePage(countryCode: string | null | undefined): Observable<boolean> {
    const cacheKey = (countryCode ?? '').trim().toUpperCase() || '__GLOBAL__';
    const cached = this.resolveCache.get(cacheKey);
    if (cached !== undefined) {
      return of(cached);
    }

    let params = new HttpParams();
    const normalized = (countryCode ?? '').trim().toUpperCase();
    if (normalized) {
      params = params.set('country_code', normalized);
    }

    return this.http
      .get<SkipPublicPageResolveResponse>(`${this.baseUrl}/resolve/`, { params })
      .pipe(
        map((response) => !!response.display_home),
        tap((allowed) => this.resolveCache.set(cacheKey, allowed)),
        catchError(() => this.resolveFromSettings(countryCode, cacheKey))
      );
  }

  clearCache(): void {
    this.settingsCache = null;
    this.settingsRequest$ = null;
    this.resolveCache.clear();
  }

  private resolveFromSettings(
    countryCode: string | null | undefined,
    cacheKey: string
  ): Observable<boolean> {
    if (this.settingsCache) {
      const allowed = resolveHomePageAccess(
        this.settingsCache.enabled,
        countryCode,
        this.settingsCache.countryOverrides
      );
      this.resolveCache.set(cacheKey, allowed);
      return of(allowed);
    }

    return this.http.get<SkipPublicPageApiRecord[]>(`${this.baseUrl}/`).pipe(
      map((records) => {
        const settings = apiRecordsToUiSettings(records, (code) => code);
        this.settingsCache = settings;
        return resolveHomePageAccess(settings.enabled, countryCode, settings.countryOverrides);
      }),
      tap((allowed) => this.resolveCache.set(cacheKey, allowed)),
      catchError(() => {
        // Fail closed: no public home when settings cannot be loaded.
        this.resolveCache.set(cacheKey, false);
        return of(false);
      })
    );
  }
}
